import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Modal from './ui/Modal';
import Button from './ui/Button';

export const SessionWarning: React.FC = () => {
  const { sessionExpiringSoon, timeUntilExpiry, extendSession, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isExtending, setIsExtending] = useState(false);

  useEffect(() => {
    if (sessionExpiringSoon && timeUntilExpiry) {
      setShowWarning(true);
      setCountdown(Math.floor(timeUntilExpiry / 1000));
    } else {
      setShowWarning(false);
      setCountdown(null);
    }
  }, [sessionExpiringSoon, timeUntilExpiry]);

  useEffect(() => {
    if (!countdown || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev && prev <= 1) {
          setShowWarning(false);
          return null;
        }
        return prev ? prev - 1 : null;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      const success = await extendSession();
      if (success) {
        setShowWarning(false);
        setCountdown(null);
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
    } finally {
      setIsExtending(false);
    }
  };

  const handleLogout = async () => {
    setShowWarning(false);
    setCountdown(null);
    await logout();
  };

  if (!showWarning) return null;

  const minutes = Math.floor((countdown || 0) / 60);
  const seconds = (countdown || 0) % 60;

  return (
    <Modal
      isOpen={showWarning}
      onClose={handleExtendSession}
      title="Session expiring soon"
      description="Extend your session to keep working without interruption."
      size="sm"
      showClose={false}
    >
      <div className="text-center mb-6 py-2">
        <div className="text-4xl font-bold text-amber-400 tabular-nums mb-2" aria-live="polite">
          {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
        </div>
        <p className="text-gray-400 text-sm">Time remaining before you&apos;re signed out.</p>
      </div>

      <div className="flex gap-3">
        <Button variant="secondary" onClick={handleLogout} className="flex-1">
          Sign out
        </Button>
        <Button variant="primary" onClick={handleExtendSession} loading={isExtending} className="flex-1">
          Extend session
        </Button>
      </div>
    </Modal>
  );
}; 